import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { 
  Truck, 
  Package, 
  MapPin, 
  Shield, 
  CheckCircle,
  Clock,
  ArrowRight
} from 'lucide-react';

export default function LogisticsPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#00887A]/5 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00887A]/10 rounded-full mb-6">
                <Truck className="w-5 h-5 text-[#00887A]" />
                <span className="text-[#00887A] font-medium text-sm">Fletes y Mandados</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight">
                Fletes y Mandados
                <span className="text-[#00887A]"> Bajo Demanda</span>
              </h1>
              <p className="text-lg text-slate-600 mt-6 leading-relaxed">
                Envía paquetes, documentos o carga de forma rápida y confiable con Mercapoli Move.
              </p>
              <p className="text-slate-600 mt-4">
                Desde mandados pequeños hasta fletes medianos, conectamos tu envío con conductores 
                y vehículos adecuados en tiempo real.
              </p>
              <Link to="/register">
                <Button className="btn-primary mt-8 h-14 px-8 text-lg">
                  Enviar ahora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=600&h=400&fit=crop" 
                alt="Logistics"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Características Principales
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Mandados urbanos</h3>
              <p className="text-slate-600">Envía documentos, comida o paquetes pequeños de forma rápida.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00887A]/10 rounded-xl flex items-center justify-center mb-4">
                <Truck className="w-7 h-7 text-[#00887A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Fletes locales</h3>
              <p className="text-slate-600">Transporta muebles, electrodomésticos y carga pesada con vehículos adecuados.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Tracking en vivo</h3>
              <p className="text-slate-600">Sigue tu envío en tiempo real desde la recogida hasta la entrega.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Entrega segura</h3>
              <p className="text-slate-600">Confirmación digital de entrega con foto y firma electrónica.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00887A]/10 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="w-7 h-7 text-[#00887A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Confirmación digital</h3>
              <p className="text-slate-600">Recibe notificaciones en cada etapa del proceso de entrega.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#00887A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
            ¿Necesitas enviar algo?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Desde un sobre hasta una mudanza completa.
          </p>
          <Link to="/register">
            <Button className="bg-white text-[#00887A] hover:bg-white/90 h-14 px-8 text-lg rounded-full">
              Solicitar Envío
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
